import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import { useSelector } from 'react-redux';
import WebsiteStore from '../../../WebsiteStore';
import { styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SectionPricing from '../../_Sections/SectionPricing';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const PRODUCT_ID = 61;
const VIEW_PRODUCT_ID = 15;

export default function AthleticCropTopPage() {
  const [t] = useTranslation('page/products/athletic-crop-top');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  if (!productData) return null

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom all-over print Athletic Crop Tops | Products')}
      description={t('Design and create your own custom printed Athletic Crop Top. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#ffafaf"
      bgStyle={{
        backgroundPosition: 'bottom right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '47%',
      }}
      tag={t('The all-over print')}
      title={t('Athletic Crop Top')}
      text={t('Turn heads in custom all-over print athletic crop tops made on-demand. Featuring a sculpted racerback fit and made from ultrasmooth Luxeform fabric, athletic crop tops combine freedom of movement with vibrant, fade-resistant prints. A stylish essential for workouts, festivals, or everyday wear.')}
      createUrl={productData.get('url_slug') + '?option_fabric=performance-knit'}
      img={require('@resources/img/pages/products/athletic-crop-top/banner-1-' + locale + '.png')}
      alt={t('Picture of Athletic Crop Top')}
      details={[
        {
          img: require('@resources/img/pages/products/athletic-crop-top/icon-1.svg?url'),
          alt: t('icon'),
          text: t('Fitted crop top with racerback cut.'),
        },
        {
          img: require('@resources/img/pages/products/athletic-crop-top/icon-2.svg?url'),
          alt: t('icon'),
          text: t('Made from Luxeform performance fabric: combining the power of spandex with lightness and comfort'),
        },
        {
          img: require('@resources/img/pages/products/athletic-crop-top/icon-3.svg?url'),
          alt: t('icon'),
          text: <Trans
            t={t}
            i18nKey={tKey('Crop length of <size/> from underarm.')}
            components={{
              size: <SizeDisplay sizes={[10.5]} sourceUnit="in" displayType="physical" />,
            }}
          />,
        },
        {
          img: require('@resources/img/pages/products/athletic-crop-top/icon-4.svg?url'),
          alt: t('icon'),
          text: t('Vibrant, permanent prints won’t fade with use or washing'),
        },
        {
          img: require('@resources/img/pages/products/athletic-crop-top/icon-5.svg?url'),
          alt: t('icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      require('@resources/img/pages/products/athletic-crop-top/gallery_0.jpg'),
      require('@resources/img/pages/products/athletic-crop-top/gallery_1.jpg'),
      require('@resources/img/pages/products/athletic-crop-top/gallery_2.jpg'),
      require('@resources/img/pages/products/athletic-crop-top/gallery_3.jpg'),
      require('@resources/img/pages/products/athletic-crop-top/gallery_4.jpg'),
    ]} />

    <SectionDropship
      title={<Trans
        t={t}
        i18nKey={tKey('<yellow>Drop ship</yellow> your athletic<br/>crop top <pink>on-demand</pink>')}
        components={{
          yellow: <span className="ty" />,
          pink: <span className="tp" />,
        }}
      />}
      text={t('Your brand and following will love athletic crop tops! At prices designed for reselling, athletic crop tops made on demand meet the needs of sellers who prefer not to stock inventory. Sell them in your eCommerce store and decide your profit margin.')}
    />

    <SectionPricing
      productId={PRODUCT_ID}
      options={['performance-knit']}
    />

    <SectionSizes
      chart="tops"
      productId={PRODUCT_ID}
      title={t('Athletic Crop Top Sizing')}
      text={t('There may be some variations in product measurements within or between orders. Each garment is individually made which can lead to minor differences in measurements. Measurements are body measurements that correspond with the garment size in US standard sizing. For in between sizes, we recommend choosing the larger size. ')}
    />

    <SectionBranding
      hasLabel
      title={t('Brand your Athletic Crop Top')}
      text={t('Branding enhances your products. Add custom labels, artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Athletic crop top with these great products to create your own unique styles.')}
      productSlugs={['mini-shorts', 'yoga-shorts', 'yoga-leggings']}
    />
  </div>
}
